import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Mail, Shield, Trash2 } from "lucide-react";

const SUPPORT_EMAIL = "help@evooluir.com";

export default function AccountDeletion() {
  const mailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    "Account & Data Deletion Request",
  )}&body=${encodeURIComponent(
    "I would like to request deletion of my Evooluir account and associated data.\n\nAccount email: \n",
  )}`;

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-2xl space-y-8 py-12">
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Trash2 className="w-5 h-5 text-primary" />
            </div>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Delete Your Evooluir Account
          </h1>
          <p className="text-sm text-muted-foreground">
            How to request deletion of your Evooluir account and associated data
          </p>
        </div>

        <Card>
          <CardHeader className="space-y-1">
            <CardTitle className="text-xl">How to request deletion</CardTitle>
            <CardDescription>
              Follow these steps to permanently delete your account
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ol className="list-decimal space-y-3 pl-5 text-sm text-foreground">
              <li>
                Send an email to{" "}
                <a
                  href={mailto}
                  className="font-medium text-primary underline underline-offset-4"
                >
                  {SUPPORT_EMAIL}
                </a>{" "}
                from the email address associated with your Evooluir account.
              </li>
              <li>
                Use the subject line{" "}
                <span className="font-medium">
                  &ldquo;Account &amp; Data Deletion Request&rdquo;
                </span>
                .
              </li>
              <li>
                Include the email address (and, if known, the username) tied to
                the account you want deleted.
              </li>
              <li>
                We will verify the request, delete your account, and confirm by
                email once complete.
              </li>
            </ol>

            <div className="rounded-md border bg-muted/40 p-4">
              <a
                href={mailto}
                className="inline-flex items-center gap-2 text-sm font-medium text-primary"
              >
                <Mail className="h-4 w-4" />
                Email {SUPPORT_EMAIL} to request deletion
              </a>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="space-y-1">
            <CardTitle className="text-xl">What data is deleted</CardTitle>
            <CardDescription>
              Data removed when your account is deleted
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-foreground">
            <p>
              Upon a verified request, we delete or anonymize your personal data
              within <span className="font-medium">30 days</span>. This includes:
            </p>
            <ul className="list-disc space-y-2 pl-5 text-muted-foreground">
              <li>Account profile (name, email, login credentials)</li>
              <li>Journal entries (gratitude, contribution, reflections)</li>
              <li>Mood, streak, and daily ritual history</li>
              <li>
                Health tracking data (weight, body metrics, supplements,
                medications, sleep, cycle, nutrition, beauty routine)
              </li>
              <li>Aeos AI chat history</li>
              <li>Device and push-notification tokens</li>
            </ul>

            <p className="pt-2">
              <span className="font-medium">Data we may retain:</span> Certain
              records may be kept after deletion where we are legally required
              to retain them (for example, payment and transaction records for
              tax, accounting, or fraud-prevention purposes) and are held only
              for the period required by law. Anonymized or aggregated data that
              can no longer identify you may be retained indefinitely.
            </p>
          </CardContent>
        </Card>

        <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <Shield className="h-3 w-3" />
          <span>Evooluir — your privacy is protected</span>
        </div>
      </div>
    </div>
  );
}
